import { DeviceCamera, EventLog, Sensor, SensorGroup, SensorReadingType } from '../';
import { UserAccessType } from '../User/UserAccessType.model';
import { WaterSchedule } from '@models/Irrigation/waterSchedule';
import { ConditionResult, TaskResults } from './TaskResult.model';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { ActionQueue } from '@models/ActionQueue/ActionQueue.model';

export class Event {
    id: number;
    name: string;
    description: string;
    userName: string;
    userGroup: string;
    userGroupID: number;
    userAccess: UserAccessType;
    eventType: EventType;
    eventTypeSelect?: string;
    sensorConditions: SensorCondition[];
    timerConditions: TimerCondition[];
    modelConditions: ModelCondition[];
    motionConditions: MotionCondition[];
    eventActions: EventAction[];
    isDisabled: boolean;
    isDisabledColor?: string;
    createdDate: Date;
    modifiedDate: Date;
    ignoreUntilDate: Date;
    evaluateStartTime: string;
    evaluateEndTime: string;
    evaluateDaysOfWeek: boolean[];
    evaluateDaysOfWeekUser?: string;
    evaluateActiveState?: string;
    evaluateActiveColor?: string;
    displayedCondition?: string;
    displayedAction?: string;
    displayedOwner?: string;
    userFeatureAccess?: { [key: string] : boolean };
    checked: boolean;
    
    tooltips?: { [key: string] : string };
    itemColor?: { [key: string] : string };

    overallActionStatus: EventActionStatus;
    lastEventLog?: EventLog;
    lastEventLogColor?: string;
    currentTaskResults?: TaskResults;

    miniChartData?: MiniChartData;

    useActionQueue: boolean;
    actionQueue?: ActionQueue;
    actionQueueId: number;
    actionQueuePosition?: number;
}

export class EventActionStatus {
    state: number;                      // 0: No status, 1: Waiting for status, 2: Not busy, 3: Busy, 4: unknown
    duration: number;                   // in minutes
}

export class EventTooltipHistory {
    eventId: number;
    changeDate: Date;
    userName: string;
    changeLog: string;
    details: string;
    eventItem: string;
}

export class EvaluateTime {
    id: number;
    name: string;
}

export class SensorCondition {
    id: number;
    eventID: number;
    sensorGroupID: number;
    sensorGroup: SensorGroup; //SensorGroupName;
    sensorReadingType: SensorReadingType;

    numberOfSamples: number;
    lastXMinutes: number;

    analysisType: AnalysisType;
    comparator: Comparator;
    threshold: number;
    logicOperator: LogicOperator;
    order: number;
}

export class TimerCondition {
    id: number;
    eventID: number;
    type: TimerEventType;
    daysOfWeek: boolean[];
    timingSingle: string;
    timingRepeat: TimingInterval;
    logicOperator: LogicOperator;
    order: number;
}

export class ModelCondition {
    id: number;
    eventID: number;
    modelName: string;
    comparator: Comparator;
    threshold: number;
    logicOperator: LogicOperator;
    order: number;
}

export class MotionCondition {
    id: number;
    eventID: number;
    deviceSerial: string;
    deviceName: string;
    sensitivity: number;
    detectionArea: string;
    postMotionSleepTime: number;
    preMotionCaptureTime: number;
    logicOperator: LogicOperator;
    order: number;
}

export class EventAction {
    id: number;
    eventID: number;
    type: ActionType;
    destination: string;
    deviceSerial: string;
    deviceName: string;

    zone: number;
    schedule?: WaterSchedule;
    scheduleName: string;
    scheduleID: number;

    taskAction: string;
    onError: string;
    mediaResolution: string;
    mediaDuration: number;
    mediaDelay: number;
    cameraId: number;
    cameraName: string;
    cameraModel: string;
    cameraPresets: string;

    mediaActionOptions: MediaActionsOptionsModel[];  

    sourceZone: number; 

    fillZone: number;                   
    fillSchedule?: WaterSchedule;   
    fillScheduleName: string;            
    fillScheduleId: number;

    drainZone: number;                  
    drainSchedule?: WaterSchedule;  
    drainScheduleName: string;              
    drainScheduleId: number;

    irrigationActionOptions: IrrigationActionOptionsModel;

    notificationSectionOptions: NotificationSectionOptionsModel;
}

export class MiniChartData {
    sensors: Sensor[];
    sensorReadingTypes: SensorReadingType[];
    sensorReadings: SensorReadingInfo[];
}

export enum EventType {
    SensorBased = 'Sensor Based',
    TimerBased = 'Timer Based',
    ModelBased = 'Model Based',
    MotionBased = 'Motion Based'
}

export enum TimerEventType {
    Single = 'Single',
    Repeat = 'Repeat'
}

export enum LogicOperator {
    And = 'And',
    Or = 'Or'
}

export enum ActionType {
    Email = 'Email',
    Url = 'Url',
    Task = 'Task',
    Irrigation = 'Irrigation',
    AppMessage = 'AppMessage',
    CaptureImage = 'CaptureImage',                               
    CaptureVideo = 'CaptureVideo',                  
    PTZCaptureImage = 'PTZCaptureImage',            
    FloodIrrigation = 'FloodIrrigation',
    PlaceHolderUnused = 'PlaceHolderUnused',
    Camera = 'Camera'
}

export enum Comparator {
    lte = '<=',
    gte = '>=',
    lt = '<',
    gt = '>',
    eq = '=',
    neq = '!='
}

export enum TimingInterval {
    EveryHalfHour = 'Every Half Hour',
    EveryHour = 'Every Hour',
    EveryTwoHours = 'Every 2 Hours',
    EveryFourHours = 'Every 4 Hours',
    EveryEightHours = 'Every 8 Hours'
}

export enum AnalysisType {
    median = 'median',
    average = 'average',
    min = 'min',
    max = 'max',
    datarate = 'data rate',
    medAbsDiff = '|medA-medB|',
    avgAbsDiff = '|avgA-avgB|',
    minAbsDiff = '|minA-minB|',
    maxAbsDiff = '|maxA-maxB|',
    groupcount = 'group count',
    singlecount = 'single count',
    trimmedMean = 'trimmed mean'
}


export class EventInfo {
    id: number;
    name: string;
    description: string;
    userName: string;
    userGroup: string;
    userGroupId: number;
    eventType: number;
    sensorConditions: SensorConditionInfo[];
    timerConditions: TimerConditionInfo[];
    modelConditions: ModelCondition[];
    motionConditions: MotionConditionInfo[];
    eventActions: EventActionInfo[];
    isDisabled: boolean;
    createdDate: Date;
    modifiedDate: Date;
    evaluateStartTime: Date;
    evaluateEndTime: Date;
    evaluateDaysOfWeek: string;
    ignoreUntilDate: Date;
    actionQueueId: number;
}

export class SensorConditionInfo {
    id: number;
    eventID: number;
    sensorGroupID: number;
    sensorReadingTypeID: number;

    numberOfSamples: number;
    lastXMinutes: number;
    
    analysisType: number;
    comparator: number;
    threshold: number;
    logicOperator: number;
    order: number;
}

export class TimerConditionInfo {
    id: number;
    eventID: number;
    type: number;
    daysOfWeek: string;
    timingSingle: Date;
    timingRepeat: number;
    logicOperator: number;
    order: number;
}

export class MotionConditionInfo {
    id: number;
    eventID: number;
    sensitivity: number;
    detectionArea: string;
    postMotionSleepTime: number;
    preMotionCaptureTime: number;
    logicOperator: number;
    deviceSerial: string;
    order: number;
}

export class EventActionInfo {
    id: number;
    eventID: number;
    type: number;
    destination: string;
    onError: string;
}

export class TaskActionModel {
    SerialNumber: string;               // KEEP UPPER Camel CASE
    TaskAction: string;                 // KEEP UPPER CASE
}

export class IrrigationActionModel {
    SerialNumber: string;               // KEEP UPPER Camel CASE
    Zone: number;                       // KEEP UPPER Camel CASE
    WaterSchedule: string;              // KEEP UPPER Camel CASE
    WaterScheduleID: number;            // KEEP UPPER Camel CASE
    ActionOptions: IrrigationActionOptionsModel; // KEEP UPPER Camel CASE
}

export class FloodIrrigationActionModel {
    SerialNumber: string;               // KEEP UPPER Camel CASE
    SourceZone: number;                 // KEEP UPPER Camel CASE
    FillZone: number;                   // KEEP UPPER Camel CASE
    FillSchedule: string;               // KEEP UPPER Camel CASE
    FillScheduleId: number;             // KEEP UPPER Camel CASE
    DrainZone: number;                  // KEEP UPPER Camel CASE
    DrainSchedule: string;              // KEEP UPPER Camel CASE
    DrainScheduleId: number;            // KEEP UPPER Camel CASE
    ActionOptions: IrrigationActionOptionsModel; // KEEP UPPER Camel CASE
}

export class CameraActionModel {
    SerialNumber: string;               // KEEP UPPER Camel CASE
    CameraId: number;                   // KEEP UPPER Camel CASE
    CameraPresets: string;              // KEEP UPPER Camel CASE
    ActionOptions: MediaActionsOptionsModel[]; // KEEP UPPER Camel CASE
}

export class MediaActionsOptionsModel {
    PosId: number;                      // KEEP UPPER Camel CASE
    Tags: string;                       // KEEP UPPER Camel CASE
    ExpireDays: number;                 // KEEP UPPER Camel CASE
    SleepAfter: number;                 // KEEP UPPER Camel CASE
    CameraMode: string;                 // KEEP UPPER Camel CASE
    VideoDuration: number;              // KEEP UPPER Camel CASE
}

export class IrrigationActionOptionsModel {
    // UseQueue: boolean;                  // KEEP UPPER Camel CASE
    // ActionQueueId: number;              // KEEP UPPER CAMEL CASE
}

// An email action's destination. The address is saved together with the options, so actions
// saved before the options existed hold the address on its own (see the mapping helper).
export class EmailActionModel {
    EmailAddress: string;               // KEEP UPPER Camel CASE
    IncludeMatchedReadings: boolean;    // KEEP UPPER Camel CASE
    IncludeAllReadings: boolean;        // KEEP UPPER Camel CASE
}

// Same, for an app message action, where the message replaces the address.
export class AppMessageActionModel {
    Message: string;                    // KEEP UPPER Camel CASE
    IncludeMatchedReadings: boolean;    // KEEP UPPER Camel CASE
    IncludeAllReadings: boolean;        // KEEP UPPER Camel CASE
}

// Which reading sections an email or app message action includes in its body.
export class NotificationSectionOptionsModel {
    IncludeMatchedReadings: boolean;    // KEEP UPPER Camel CASE
    IncludeAllReadings: boolean;        // KEEP UPPER Camel CASE
}

export class EventIgnore {
    eventIds: number[];
    ignoreUntilDate: Date;
}